import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams, useRouteContext } from '@tanstack/react-router';
import { useState, type FormEvent } from 'react';
import { tags as tagContracts } from '@bb/contracts';
import { Button, ConfirmDialog } from '@bb/ui';
import { ApiError, type TagSummary } from '../api/client.ts';
import { usePermissions } from '../features/access/usePermissions.ts';

const TYPE_LABEL: Record<string, [string, string]> = {
  campaign: ['campaign', 'campaigns'],
  canvas: ['canvas', 'canvases'],
  segment: ['segment', 'segments'],
  template: ['template', 'templates'],
  content_block: ['content block', 'content blocks'],
  event_definition: ['event', 'events'],
  attribute_definition: ['attribute', 'attributes'],
  media_asset: ['media file', 'media files'],
};
const usageText = (type: string, n: number) => `${n} ${(TYPE_LABEL[type] ?? [type, type])[n === 1 ? 0 : 1]}`;
const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/** Settings → Tags (BB-18): every tag with where it is used; create, rename, delete. */
export function TagsPage() {
  const { environmentId } = useParams({ from: '/env/$environmentId/settings/tags' });
  const { api } = useRouteContext({ from: '/env/$environmentId/settings/tags' });
  const queryClient = useQueryClient();
  const { has } = usePermissions();
  const canManage = tagContracts.TAG_MANAGE_PERMISSIONS.some((p) => has(p));
  const queryKey = ['tags', environmentId];
  const list = useQuery({ queryKey, queryFn: () => api.listTags(environmentId) });
  const refresh = () => queryClient.invalidateQueries({ queryKey });
  const [name, setName] = useState('');
  const [editing, setEditing] = useState<{ id: string; name: string } | null>(null);
  const [deleting, setDeleting] = useState<TagSummary | null>(null);
  const create = useMutation({ mutationFn: () => api.createTag(environmentId, { name: name.trim() }), onSuccess: () => (setName(''), refresh()) });
  const rename = useMutation({ mutationFn: (e: { id: string; name: string }) => api.updateTag(environmentId, e.id, { name: e.name.trim() }), onSuccess: () => (setEditing(null), refresh()) });
  const remove = useMutation({ mutationFn: (tag: TagSummary) => api.deleteTag(environmentId, tag.id, tag.total > 0), onSuccess: () => (setDeleting(null), refresh()) });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (name.trim()) create.mutate();
  };

  return (
    <section>
      <h1>Tags</h1>
      <p className="hint">Tags group campaigns, canvases, segments and other objects; lists filter by them and search finds by them.</p>
      {list.isPending ? (
        <p aria-busy="true">Loading tags…</p>
      ) : list.isError ? (
        <p role="alert">Tags could not be loaded.</p>
      ) : list.data.length === 0 ? (
        <p className="empty-state">No tags yet.</p>
      ) : (
        <table className="table">
          <thead>
            <tr>
              <th scope="col">Name</th>
              <th scope="col">Used in</th>
              <th scope="col">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.data.map((tag) => (
              <tr key={tag.id}>
                <td>
                  {editing?.id === tag.id ? (
                    <form
                      aria-label={`Rename ${tag.name}`}
                      onSubmit={(e) => {
                        e.preventDefault();
                        if (editing.name.trim()) rename.mutate(editing);
                      }}
                    >
                      <input aria-label="New name" value={editing.name} onChange={(e) => setEditing({ id: tag.id, name: e.target.value })} />
                      <button type="submit" disabled={rename.isPending}>
                        Save
                      </button>
                      <button type="button" onClick={() => setEditing(null)}>
                        Cancel
                      </button>
                      {rename.isError && <span role="alert">{errorText(rename.error)}</span>}
                    </form>
                  ) : (
                    tag.name
                  )}
                </td>
                <td>{tag.total === 0 ? <span className="hint">Not used</span> : Object.entries(tag.usage).map(([type, n]) => usageText(type, n)).join(', ')}</td>
                <td>
                  {canManage && (
                    <>
                      <button type="button" aria-label={`Rename ${tag.name}`} onClick={() => setEditing({ id: tag.id, name: tag.name })}>
                        Rename
                      </button>{' '}
                      <button
                        type="button"
                        aria-label={`Delete ${tag.name}`}
                        disabled={tag.total > 0 && !has('env.manage')}
                        title={tag.total > 0 && !has('env.manage') ? 'Deleting a tag in use needs env.manage' : undefined}
                        onClick={() => setDeleting(tag)}
                      >
                        Delete
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canManage && (
        <form className="stack-form" aria-label="Create tag" onSubmit={submit}>
          <label>
            Tag name
            <input value={name} onChange={(e) => setName(e.target.value)} placeholder="pricealert" maxLength={64} />
          </label>
          <Button type="submit" disabled={create.isPending || !name.trim()}>
            Create tag
          </Button>
          {create.isError && <span role="alert">{errorText(create.error)}</span>}
        </form>
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => !open && setDeleting(null)}
        title={`Delete tag “${deleting?.name ?? ''}”?`}
        confirmLabel="Delete tag"
        busy={remove.isPending}
        onConfirm={() => deleting && remove.mutate(deleting)}
      >
        {deleting && deleting.total > 0 ? (
          <p>It is used by {deleting.total} objects. Deleting removes it from all of them; filters and exclusions that use it stop matching.</p>
        ) : (
          <p>The tag is not used anywhere.</p>
        )}
        {remove.isError && <p role="alert">{errorText(remove.error)}</p>}
      </ConfirmDialog>
    </section>
  );
}
